import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Search, Loader2, Sparkles, LifeBuoy, ArrowLeft, Send, Ticket } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/context/AuthContext";

export default function Help() {
  const { slug } = useParams();
  const [articles, setArticles] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [active, setActive] = useState(null);
  const [ask, setAsk] = useState("");
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState("");
  const [ticket, setTicket] = useState({ subject: "", message: "", open: false });

  useEffect(() => { apiFetch("/help/articles", { auth: false }).then(setArticles).catch(() => {}); }, []);
  useEffect(() => { if (slug) apiFetch(`/help/articles/${slug}`, { auth: false }).then(setActive).catch(() => {}); }, [slug]);

  const search = async () => {
    if (!query.trim()) { setResults(null); return; }
    setResults(await apiFetch("/help/search", { method: "POST", body: { query }, auth: false }));
  };
  const runAssistant = async () => {
    if (!ask.trim()) return;
    setBusy("ai"); setAnswer(null);
    try { setAnswer(await apiFetch("/help/assistant", { method: "POST", body: { message: ask }, auth: false })); }
    catch { toast.error("Assistant unavailable."); } setBusy("");
  };
  const submitTicket = async () => {
    if (!ticket.subject.trim() || !ticket.message.trim()) return;
    setBusy("ticket");
    try { await apiFetch("/help/tickets", { method: "POST", body: { subject: ticket.subject, message: ticket.message }, auth: false }); toast.success("Ticket created — we'll be in touch."); setTicket({ subject: "", message: "", open: false }); }
    catch { toast.error("Couldn't create ticket."); } setBusy("");
  };

  const list = results ?? articles;

  return (
    <div className="min-h-screen relative">
      <div className="pointer-events-none absolute -top-40 right-0 w-[40rem] h-[40rem] bg-[#0055FF]/10 blur-[150px] rounded-full" />
      <header className="relative z-10 max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3"><div className="w-9 h-9 rounded-xl bg-[#0055FF] flex items-center justify-center font-display font-extrabold text-lg">A</div><span className="font-display text-xl font-bold">ArcaCentum<span className="text-[#00F0FF]">.ai</span> <span className="text-slate-500 text-sm">Help</span></span></Link>
        <Link to="/" className="font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-2"><ArrowLeft size={14} /> Home</Link>
      </header>

      <main className="relative z-10 max-w-4xl mx-auto px-6 pb-24">
        {active ? (
          <article data-testid="help-article">
            <Link to="/help" onClick={() => setActive(null)} className="text-[#00F0FF] text-sm inline-flex items-center gap-1 mb-6"><ArrowLeft size={13} /> All articles</Link>
            <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{active.category}</p>
            <h1 className="font-display font-extrabold text-4xl tracking-tight mt-2 mb-6">{active.title}</h1>
            <p className="text-slate-300 text-lg leading-relaxed whitespace-pre-wrap">{active.body}</p>
          </article>
        ) : (
          <>
            <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight">How can we help?</h1>
            <div className="mt-6 flex items-center gap-3 bg-black/40 border border-white/10 rounded-2xl px-5 focus-within:border-[#0055FF] transition-colors duration-300">
              <Search size={18} className="text-slate-500" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search help articles…" className="flex-1 bg-transparent outline-none py-4 text-white" data-testid="help-search-input" />
              <button onClick={search} className="text-sm bg-[#0055FF] text-white px-4 py-2 rounded-xl" data-testid="help-search-btn">Search</button>
            </div>

            {/* AI assistant */}
            <div className="mt-6 rounded-2xl border border-[#0055FF]/30 bg-[#0055FF]/10 p-5" data-testid="help-assistant">
              <p className="font-display font-bold flex items-center gap-2 mb-2"><Sparkles size={16} className="text-[#00F0FF]" /> Ask the AI assistant</p>
              <div className="flex gap-2">
                <input value={ask} onChange={(e) => setAsk(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runAssistant()} placeholder="Ask anything about ArcaCentum…" className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#0055FF]" data-testid="help-assistant-input" />
                <button onClick={runAssistant} disabled={busy === "ai"} className="bg-white text-black px-4 rounded-xl font-semibold disabled:opacity-40" data-testid="help-assistant-btn">{busy === "ai" ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</button>
              </div>
              {answer && (
                <div className="mt-4 text-sm" data-testid="help-assistant-answer">
                  <p className="text-slate-200 whitespace-pre-wrap">{answer.answer}</p>
                  {!answer.resolved && (
                    <button onClick={() => setTicket((t) => ({ ...t, open: true }))} className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#00F0FF]" data-testid="help-escalate"><Ticket size={13} /> Still stuck? Create a support ticket</button>
                  )}
                </div>
              )}
            </div>

            {/* Articles */}
            <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid="help-articles">
              {list.map((a) => (
                <Link key={a.slug} to={`/help/${a.slug}`} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5 hover:border-[#0055FF] transition-colors duration-300" data-testid={`help-article-${a.slug}`}>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{a.category}</p>
                  <p className="font-display font-bold mt-1">{a.title}</p>
                  <p className="text-slate-500 text-sm mt-1 line-clamp-2">{a.body}</p>
                </Link>
              ))}
              {list.length === 0 && <p className="text-slate-500">No articles match your search.</p>}
            </div>
          </>
        )}
      </main>

      {/* Ticket modal */}
      {ticket.open && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-6" data-testid="help-ticket-modal">
          <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0a0a0a] p-6">
            <p className="font-display font-bold text-xl mb-4">Create a support ticket</p>
            <input value={ticket.subject} onChange={(e) => setTicket((t) => ({ ...t, subject: e.target.value }))} placeholder="Subject" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm mb-3 outline-none focus:border-[#0055FF]" data-testid="help-ticket-subject" />
            <textarea value={ticket.message} onChange={(e) => setTicket((t) => ({ ...t, message: e.target.value }))} rows={4} placeholder="Describe your issue…" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm mb-4 outline-none focus:border-[#0055FF] resize-none" data-testid="help-ticket-message" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setTicket((t) => ({ ...t, open: false }))} className="px-4 py-2 text-slate-400 text-sm">Cancel</button>
              <button onClick={submitTicket} disabled={busy === "ticket"} className="bg-[#0055FF] text-white px-5 py-2 rounded-xl text-sm font-semibold disabled:opacity-40" data-testid="help-ticket-submit">Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
